import type { WorkOrderActKc2Data, WorkOrderOtherPriceData, WorkOrderPriceData } from 'gql/graphql';
import { ChapterEnum, ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { Button, ContextMenu, IconKebab, Tooltip } from 'uikit';

import * as R from 'ramda';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';

import { useDeletableWorkOrderItems } from '../hooks/useDeletableWorkOrderItems';

import { useWorkOrderRowMenuItems } from './useWorkOrderRowMenuItems';

interface IWorkOrderRowMenuProps {
    row: WorkOrderPriceData | (WorkOrderOtherPriceData & { type: string });
    hasActs: boolean;
    chapter: ChapterEnum;
    actsData: WorkOrderActKc2Data[];
    workOrderStatus: WorkOrderStatusEnum | undefined;
    contractStatus?: ContractStatusEnum;
    isAllAktKC2Accepted?: boolean;
    isDisabledByProjectStatus?: boolean;
    buttonStyles?: CSSProperties;
}

type Item = {
    label: string;
    action: () => void;
    active?: boolean;
};

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');

export const WorkOrderRowMenu = ({
    row,
    contractStatus,
    hasActs,
    workOrderStatus,
    isAllAktKC2Accepted,
    isDisabledByProjectStatus,
    chapter,
    buttonStyles,
    actsData,
}: IWorkOrderRowMenuProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useRef(null);
    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const deletableItems = useDeletableWorkOrderItems(row, actsData, workOrderStatus);

    const items: Item[] = useWorkOrderRowMenuItems(row, hasActs, chapter, workOrderStatus, deletableItems);

    const isContractStatusWork = contractStatus === ContractStatusEnum.InImplementation;
    const isStatusWorkOrderInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;
    const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const isDisabledBtn = !!(
        chapter !== ChapterEnum.Others &&
        (isDisabledByProjectStatus ||
            !isContractStatusWork ||
            isWorkOrderStatusDelayed ||
            isWorkOrderStatusCompleted ||
            !items.length ||
            (isStatusWorkOrderInProgress && !hasActs) ||
            (isStatusWorkOrderInProgress && isAllAktKC2Accepted) ||
            row.isExcludedPrice)
    );

    const handleItemOnClick = useCallback(
        (item) => () => {
            const action = getItemOnClick(item) as () => void;

            action();
            setIsOpen(false);
        },
        [],
    );
    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                style={buttonStyles ?? { marginTop: 12, marginBottom: 12, marginLeft: 4 }}
                onClick={handleOnClick}
                ref={ref}
                disabled={isDisabledBtn}
            />

            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={handleItemOnClick}
                onClickOutside={handleOnClick}
                getItemAttributes={getMenuItemAttributes}
                onMouseLeave={onMenuMouseLeave}
                onMouseMove={onMenuMouseMove}
            />

            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </>
    );
};

WorkOrderRowMenu.displayName = 'WorkOrderRowMenu';
